import { useMemo } from "react"
import { Navigation, Virtual } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import ProductCard from "./ProductCard/ProductCard"
import { Link } from "react-router-dom"
import { ChevronRight } from "lucide-react"
import { useMediaQuery, useTheme } from "@mui/material"

export default function ProductWithSwiper({
  title = "",
  data = [],
  type = "",
  viewAllPath = "",
  mobileHeight = 360,
  desktopHeight = 500,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const safeData = Array.isArray(data) ? data : []

  const navSlug = useMemo(
    () =>
      (title || "slider")
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9\-]/g, ""),
    [title]
  )
  const prevClass = useMemo(() => `prev-${navSlug}`, [navSlug])
  const nextClass = useMemo(() => `next-${navSlug}`, [navSlug])

  const maxSlidesPerView = 5
  const enableNav = safeData.length > maxSlidesPerView
  const allowTouchMove = safeData.length > 2

  const swiperProps = useMemo(
    () => ({
      className: "swiper tf-sw-product-sell wrap-sw-over",
      slidesPerView: 5,
      spaceBetween: 30,
      breakpoints: {
        1024: { slidesPerView: 5, spaceBetween: 30 },
        640: { slidesPerView: 3, spaceBetween: 20 },
        0: { slidesPerView: 2, spaceBetween: 15 },
      },
      modules: [Navigation, Virtual],
      navigation: enableNav
        ? { prevEl: `.${prevClass}`, nextEl: `.${nextClass}` }
        : false,
      virtual: safeData.length > 24,
      allowTouchMove,
      watchOverflow: true,
      observeParents: true,
      observer: true,
      preloadImages: false,
      updateOnWindowResize: true,
    }),
    [enableNav, allowTouchMove, prevClass, nextClass, safeData.length]
  )

  const sectionMinHeight = isMobile ? mobileHeight : desktopHeight

  return (
    <section
      className={type === "related" ? "flat-spacing-10 pb-5 pt-30" : ""}
      style={{ minHeight: sectionMinHeight }}
    >
      <div className="container">
        <div style={{ marginBottom: "1rem", marginTop: 10 }}>
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ marginBottom: "0.25rem" }}
          >
            <h2
              className="fw-bold m-0"
              style={{ fontSize: "18px", lineHeight: 1.1 }}
            >
              {title}
            </h2>

            {viewAllPath && (
              <Link
                to={viewAllPath}
                className="d-flex align-items-center text-danger text-decoration-none"
                style={{ color: "#CE464A", fontSize: 14, fontWeight: 600 }}
              >
                Lihat Semua <ChevronRight size={16} className="ms-1" />
              </Link>
            )}
          </div>
        </div>

        <div
          className="hover-sw-nav hover-sw-2"
          style={{ marginTop: 0, paddingTop: 0 }}
        >
          <Swiper {...swiperProps}>
            {safeData.map((product, i) => (
              <SwiperSlide
                key={product.id ?? product.slug ?? i}
                virtualIndex={i}
                className="swiper-slide"
                style={{ marginTop: 0, paddingTop: 0 }}
              >
                <div style={{ marginTop: 0 }}>
                  <ProductCard product={product} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {enableNav && (
            <>
              <div
                className={`nav-sw nav-prev-slider nav-prev-product box-icon w_46 round ${prevClass}`}
                style={{ left: 0, right: "auto" }}
              >
                <span className="icon icon-arrow-left" />
              </div>
              <div
                className={`nav-sw nav-next-slider nav-next-product box-icon w_46 round ${nextClass}`}
                style={{ right: 0, left: "auto" }}
              >
                <span className="icon icon-arrow-right" />
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
